import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ amount: string, currencyCode: string }} Money
 * @typedef {{ title: string, quantity: number, image: string | null, total: Money }} Line
 * @typedef {Line & {
 *   variantTitle: string | null,
 *   properties: Array<{ key: string, value: string }>,
 *   contents: Line[], personalise: Line[], other: Line[], delivery: Line | null
 * }} Box
 * @typedef {{
 *   name: string, status: 'awaiting' | 'approved' | 'declined' | 'paid', createdAt: string,
 *   total: Money, payUrl: string | null, boxes: Box[]
 * }} Preorder
 */

/** Box properties the cards show in their own places, as the cart does. */
const PROPERTY_MESSAGE = 'Message';
const PROPERTY_DATE = 'Delivery date';

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text] - Set as text, never as markup: titles and messages come from customers
 */
function el(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

/**
 * @param {string} template - A translation with [placeholders]
 * @param {Record<string, string>} values
 */
const fill = (template, values) =>
  Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template ?? '');

/**
 * The customer's pre-orders on the My pre-orders page (sections/uradora-preorders.liquid),
 * fetched from the pre-order middleware and laid out with the cart's box cards.
 */
class UradoraPreorders extends HTMLElement {
  /**
   * Translated text from the section's <template data-strings>, keyed by data-string. Read as
   * text so the browser decodes the entities Shopify's t filter escapes.
   * @type {Record<string, string>}
   */
  #strings = {};

  connectedCallback() {
    const template = this.querySelector('template[data-strings]');
    if (template instanceof HTMLTemplateElement) {
      for (const element of template.content.querySelectorAll('[data-string]')) {
        if (element instanceof HTMLElement) this.#strings[element.dataset.string ?? ''] = element.textContent?.trim() ?? '';
      }
    }

    this.#showSubmitted();
    if (this.querySelector('[data-list]')) this.#load();
  }

  /** Confirms a pre-order the customer has just sent, named in ?submitted= */
  #showSubmitted() {
    const name = new URLSearchParams(window.location.search).get('submitted');
    const banner = this.querySelector('[data-submitted]');
    const title = this.querySelector('[data-submitted-title]');
    if (!name || !(banner instanceof HTMLElement) || !(title instanceof HTMLElement)) return;

    title.textContent = fill(title.dataset.text ?? '', { name });
    banner.hidden = false;
  }

  async #load() {
    const list = this.querySelector('[data-list]');
    const empty = this.querySelector('[data-empty]');
    const failed = this.querySelector('[data-error]');
    if (!(list instanceof HTMLElement)) return;

    try {
      const response = await fetch(this.dataset.endpoint ?? '', { headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error(result.error || `Pre-orders request failed (${response.status})`);

      /** @type {Preorder[]} */
      const preorders = result.preorders;
      list.replaceChildren(...preorders.map((preorder) => this.#preorder(preorder)));
      if (empty instanceof HTMLElement) empty.hidden = preorders.length > 0;
      this.#showCount(preorders.length);
    } catch (error) {
      console.error(error);
      list.replaceChildren();
      if (failed instanceof HTMLElement) failed.hidden = false;
    } finally {
      list.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * The number of pre-orders in the bubble beside the title, as the cart page shows its boxes,
   * with a spoken version for screen readers.
   * @param {number} count
   */
  #showCount(count) {
    const bubble = this.querySelector('[data-count]');
    const label = this.querySelector('[data-count-label]');
    if (!(bubble instanceof HTMLElement) || !(label instanceof HTMLElement)) return;

    bubble.textContent = String(count);
    bubble.hidden = count === 0;

    const form = new Intl.PluralRules(document.documentElement.lang || undefined).select(count);
    const template = this.#strings[`count.${form}`] || this.#strings['count.other'];
    label.textContent = count > 0 ? fill(template, { number: String(count) }) : '';
  }

  /** @param {Preorder} preorder */
  #preorder(preorder) {
    const strings = this.#strings;
    const card = el('article', 'upreorder');

    const heading = el('div', 'upreorder__heading');
    heading.append(
      el('h2', 'upreorder__name', fill(strings.name, { name: preorder.name })),
      el('p', 'upreorder__date', fill(strings.submitted_on, { date: this.#date(preorder.createdAt) }))
    );
    const header = el('header', 'upreorder__header');
    header.append(heading, el('p', `upreorder-chip upreorder-chip--${preorder.status}`, strings[`status.${preorder.status}`]));

    const list = el('ol', 'ucart-boxes__list');
    list.setAttribute('role', 'list');
    list.append(...preorder.boxes.map((box, index) => this.#box(box, index + 1)));
    const boxes = el('div', 'ucart-boxes ucart-boxes--page');
    boxes.append(list);

    const total = el('p', 'upreorder__total');
    total.append(el('span', 'upreorder__total-label', strings.order_total), el('span', 'upreorder__total-value', this.#money(preorder.total)));
    const footer = el('footer', 'upreorder__footer');
    footer.append(total);

    const payUrl = safeUrl(preorder.payUrl);
    if (payUrl) {
      const pay = el('a', 'upreorders__button', strings.pay_now);
      pay.setAttribute('href', payUrl);
      footer.append(pay);
    }

    card.append(header, el('p', 'upreorder__status-text', strings[`status_text.${preorder.status}`]), boxes, footer);
    return card;
  }

  /**
   * A box card laid out like the cart's, without the stepper and bin.
   * @param {Box} box
   * @param {number} number
   */
  #box(box, number) {
    const strings = this.#strings;
    const message = box.properties.find(({ key }) => key === PROPERTY_MESSAGE)?.value;
    const date = box.properties.find(({ key }) => key === PROPERTY_DATE)?.value;

    const meta = [];
    if (box.variantTitle) meta.push(el('p', 'ucart-line__meta', box.variantTitle));
    for (const { key, value } of box.properties) {
      if (key !== PROPERTY_MESSAGE && key !== PROPERTY_DATE) meta.push(el('p', 'ucart-line__meta', `${key}: ${value}`));
    }
    if (message && box.personalise.length === 0) meta.push(el('p', 'ucart-line__meta', fill(strings.message, { message })));

    const lines = el('div', 'ucart-box__lines');
    lines.append(this.#line(box, 'box', meta));
    for (const addon of [...box.contents, ...box.personalise, ...box.other]) {
      const note = message && box.personalise.includes(addon) ? [el('p', 'ucart-line__meta', fill(strings.message, { message }))] : [];
      lines.append(this.#line(addon, 'addon', note));
    }

    const summary = el('div', 'ucart-box__summary');
    if (box.delivery) {
      const delivery = el('div', 'ucart-sum');
      delivery.append(
        el('p', 'ucart-sum__label', fill(strings.shipping, { method: box.delivery.title })),
        el('p', 'ucart-sum__value', `+${this.#money(box.delivery.total)}`)
      );
      if (date) delivery.append(el('p', 'ucart-sum__note', fill(strings.delivery_date, { date })));
      summary.append(delivery);
    }

    const parts = [box, ...box.contents, ...box.personalise, ...box.other, ...(box.delivery ? [box.delivery] : [])];
    const total = el('div', 'ucart-sum ucart-sum--total');
    total.append(el('p', 'ucart-sum__label', strings.total), el('p', 'ucart-sum__value', this.#sum(parts)));
    summary.append(total);

    const badge = el('span', 'ucart-box__number', String(number));
    badge.setAttribute('aria-hidden', 'true');

    const card = el('li', 'ucart-box');
    card.append(badge, lines, summary);
    return card;
  }

  /**
   * @param {Line} line
   * @param {'box' | 'addon'} kind
   * @param {HTMLElement[]} meta - Lines under the title
   */
  #line(line, kind, meta) {
    const media = el('div', 'ucart-line__media');
    if (line.image) {
      const image = el('img');
      image.setAttribute('src', line.image);
      image.setAttribute('alt', '');
      image.setAttribute('loading', 'lazy');
      image.setAttribute('width', '300');
      image.setAttribute('height', '300');
      media.append(image);
    }

    const title = el('p', 'ucart-line__title', `${line.title} `);
    title.append(el('span', 'ucart-line__count', `x ${line.quantity}`));
    const details = el('div', 'ucart-line__details');
    details.append(title, ...meta);

    const price = this.#money(line.total);
    const row = el('div', `ucart-line ucart-line--${kind}`);
    row.append(media, details, el('p', 'ucart-line__price', kind === 'addon' ? `+${price}` : price));
    return row;
  }

  /** @param {Line[]} lines */
  #sum(lines) {
    const currency = lines[0]?.total.currencyCode ?? '';
    const minor = lines.reduce((sum, line) => sum + (convertMoneyToMinorUnits(line.total.amount, currency) ?? 0), 0);
    return this.#format(minor, currency);
  }

  /** @param {Money} money */
  #money(money) {
    return this.#format(convertMoneyToMinorUnits(money.amount, money.currencyCode) ?? 0, money.currencyCode);
  }

  /**
   * Formats like Liquid's money_without_trailing_zeros, as the cart does.
   * @param {number} minor - Amount in minor units
   * @param {string} currency
   */
  #format(minor, currency) {
    let format = this.dataset.moneyFormat || '{{amount}}';
    const unit = convertMoneyToMinorUnits('1', currency) ?? 100;
    if (minor % unit === 0) {
      format = format
        .replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}')
        .replace(/{{\s*amount_with_comma_separator\s*}}/, '{{amount_no_decimals_with_comma_separator}}');
    }
    return formatMoney(minor, format, currency);
  }

  /** @param {string} iso */
  #date(iso) {
    return new Date(iso).toLocaleDateString(document.documentElement.lang || undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
}

/**
 * Only links to an https address, whatever the response holds.
 * @param {string | null} url
 */
function safeUrl(url) {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

if (!customElements.get('uradora-preorders')) {
  customElements.define('uradora-preorders', UradoraPreorders);
}
